<template>
  <div
    class="tavus-audio-conversation"
    :class="{ 'has-start-trigger': showStartButton }"
  >
    <!-- Floating Start Button - shown in the top right -->
    <div v-if="showStartButton" class="start-chat-trigger">
      <button class="start-chat-trigger-btn" @click="startConversation">
        <img
          src="@/assets/site/images/gift-assistant.gif"
          alt="Gift Assistant"
          class="start-chat-trigger-gif"
        />
        <span class="start-chat-trigger-text">Live Chat Wizard</span>
      </button>
    </div>

    <!-- Login-required modal: shown when an unlogged visitor clicks the chat trigger. -->
    <div
      v-if="needsLogin"
      class="govava-login-modal"
      role="dialog"
      aria-modal="true"
      @click.self="dismissLoginPrompt"
    >
      <div class="govava-login-card">
        <button
          class="govava-login-close"
          type="button"
          aria-label="Close"
          @click="dismissLoginPrompt"
        >
          ×
        </button>
        <div class="govava-login-icon">
          <img src="@/assets/site/images/gift-assistant.gif" alt="Ava" />
        </div>
        <h3 class="govava-login-title">Say hi to Ava</h3>
        <p class="govava-login-sub">
          Ava is your personal gifting assistant — she remembers who you're
          shopping for, what they love, and the gifts you've already considered.
          To keep that running across your visits, please sign in.
        </p>
        <div class="govava-login-actions">
          <button class="govava-login-primary" type="button" @click="goToLogin">
            Sign in to chat with Ava
          </button>
          <button
            class="govava-login-secondary"
            type="button"
            @click="dismissLoginPrompt"
          >
            Not now
          </button>
        </div>
        <p class="govava-login-meta">
          New here? Signing in will create your account in one tap.
        </p>
      </div>
    </div>

    <!-- Cooldown modal: shown when user clicks chat trigger before the 2m 30s cooldown expires -->
    <div
      v-if="isCooldownModalOpen"
      class="govava-cooldown-modal"
      role="dialog"
      aria-modal="true"
      @click.self="closeCooldownModal"
    >
      <div class="govava-cooldown-card">
        <button
          class="govava-cooldown-close"
          type="button"
          aria-label="Close"
          @click="closeCooldownModal"
        >
          <svg
            width="18"
            height="18"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            stroke-width="2.5"
            stroke-linecap="round"
            stroke-linejoin="round"
          >
            <line x1="18" y1="6" x2="6" y2="18"></line>
            <line x1="6" y1="6" x2="18" y2="18"></line>
          </svg>
        </button>

        <div class="govava-cooldown-icon">
          <div class="govava-cooldown-avatar-ring">
            <img src="@/assets/site/images/gift-assistant.gif" alt="Ava" />
          </div>
          <div
            class="govava-cooldown-badge"
            :class="{ 'badge-ready': cooldownRemainingSeconds <= 0 }"
          >
            <svg
              v-if="cooldownRemainingSeconds > 0"
              width="15"
              height="15"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              stroke-width="2.5"
              stroke-linecap="round"
              stroke-linejoin="round"
            >
              <path d="M5 22h14"></path>
              <path d="M5 2h14"></path>
              <path
                d="M17 22v-4.172a2 2 0 0 0-.586-1.414L12 12l-4.414 4.414A2 2 0 0 0 7 17.828V22"
              ></path>
              <path
                d="M7 2v4.172a2 2 0 0 0 .586 1.414L12 12l4.414-4.414A2 2 0 0 0 17 6.172V2"
              ></path>
            </svg>
            <svg
              v-else
              width="15"
              height="15"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              stroke-width="2.8"
              stroke-linecap="round"
              stroke-linejoin="round"
            >
              <polyline points="20 6 9 17 4 12"></polyline>
            </svg>
          </div>
        </div>

        <h3 class="govava-cooldown-title">
          {{
            cooldownRemainingSeconds > 0
              ? "Ava is Catching Her Breath"
              : "Ava is Ready to Chat!"
          }}
        </h3>
        <p class="govava-cooldown-sub">
          {{
            cooldownRemainingSeconds > 0
              ? "Please wait for the cooldown timer before starting another chat session. Ava will be ready to help you find gifts shortly!"
              : "The cooldown period has finished. You can now start a new live conversation with Ava!"
          }}
        </p>

        <div class="govava-cooldown-timer-box">
          <div class="govava-cooldown-timer-header">
            <span class="govava-cooldown-timer-label">Cooldown Status</span>
            <span
              class="govava-cooldown-timer-status"
              v-if="cooldownRemainingSeconds > 0"
            >
              <span class="govava-cooldown-pulse-dot"></span> Cooling down
            </span>
            <span class="govava-cooldown-timer-status ready" v-else>
              <span class="govava-cooldown-ready-dot"></span> Ready to chat
            </span>
          </div>

          <div
            class="govava-cooldown-timer-display"
            :class="{ 'display-ready': cooldownRemainingSeconds <= 0 }"
          >
            <svg
              v-if="cooldownRemainingSeconds > 0"
              width="26"
              height="26"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              stroke-width="2.2"
              stroke-linecap="round"
              stroke-linejoin="round"
              class="cooldown-clock-icon"
            >
              <circle cx="12" cy="12" r="10"></circle>
              <polyline points="12 6 12 12 16 14"></polyline>
            </svg>
            <span class="govava-cooldown-timer-digits">
              {{
                cooldownRemainingSeconds > 0 ? formattedCooldownTime : "Ready!"
              }}
            </span>
          </div>

          <div class="govava-cooldown-progress-bar">
            <div
              class="govava-cooldown-progress-fill"
              :class="{ 'fill-ready': cooldownRemainingSeconds <= 0 }"
              :style="{ width: cooldownProgressPercent + '%' }"
            ></div>
          </div>
        </div>

        <div class="govava-cooldown-actions">
          <button
            v-if="cooldownRemainingSeconds <= 0"
            class="govava-cooldown-btn govava-btn-ready"
            type="button"
            @click="startFromCooldown"
          >
            <svg
              width="18"
              height="18"
              viewBox="0 0 24 24"
              fill="currentColor"
              style="margin-right: 8px"
            >
              <polygon points="5 3 19 12 5 21 5 3"></polygon>
            </svg>
            Start Conversation Now
          </button>
          <button
            v-else
            class="govava-cooldown-btn govava-btn-waiting"
            type="button"
            @click="closeCooldownModal"
          >
            <svg
              width="16"
              height="16"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              stroke-width="2.5"
              stroke-linecap="round"
              stroke-linejoin="round"
              style="margin-right: 8px"
            >
              <polyline points="20 6 9 17 4 12"></polyline>
            </svg>
            Got it, I'll wait
          </button>
        </div>
      </div>
    </div>

    <!-- Single persistent Avatar — rendered once a session has started
         and never re-mounted across the gift-grid transition. Anam's
         streamToVideoElement() binds to <video id="anam-avatar-video">
         here and that element survives until the session truly ends. -->
    <!-- Fatal-error panel. Deliberately a SIBLING of the avatar container,
         not a child: on an unrecoverable error the user should see the
         message alone, with no avatar frame, spinner or control bar
         flashing around it. -->
    <div
      v-if="connectionStatus === 'error' && !shouldShowGiftGrid"
      class="centered-avatar-container"
    >
      <div class="avatar-card avatar-large avatar-error-state">
        <div class="avatar-loading-text">
          <div>{{ connectionStatusText || "No Govava Avatar Found" }}</div>
          <button class="avatar-error-retry" @click="dismissFatalError">
            Close
          </button>
        </div>
      </div>
    </div>

    <div
      v-show="!showStartButton && connectionStatus !== 'error'"
      class="centered-avatar-container"
      :class="{ 'with-grid': shouldShowGiftGrid }"
    >
      <div
        class="avatar-card"
        :class="shouldShowGiftGrid ? 'avatar-medium' : 'avatar-large'"
      >
        <div id="video-container">
          <div id="remote-videos-full" class="avatar-video">
            <video id="anam-avatar-video" autoplay playsinline></video>
          </div>
        </div>
        <div
          v-if="connectionStatus === 'connecting'"
          class="avatar-loading-indicator"
        >
          <div class="avatar-loading-spinner"></div>
        </div>
        <div
          v-else-if="connectionStatus === 'disconnected' && !shouldShowGiftGrid"
          class="avatar-loading-text"
        >
          Govava Avatar Disconnected
        </div>
        <div class="avatar-controls-bar" v-if="isConnected"></div>
        <div class="avatar-controls" v-if="isConnected">
          <button class="avatar-control-btn" @click="toggleMute">
            <i
              :class="isMuted ? 'fas fa-microphone-slash' : 'fas fa-microphone'"
            ></i>
            <span>{{ isMuted ? "Unmute" : "Mute" }}</span>
          </button>
          <button class="avatar-control-btn" @click="reduceSound">
            <i
              :class="isSoundReduced ? 'fas fa-expand' : 'fas fa-compress'"
            ></i>
            <span>{{ isSoundReduced ? "Normal" : "Reduce" }}</span>
          </button>
          <button
            class="avatar-control-btn leave-btn-outline"
            @click="leaveConversationKeepingGifts"
          >
            <i class="fas fa-sign-out-alt"></i>
            <span>Leave</span>
          </button>
        </div>
        <div
          v-if="isSearchingGifts && !shouldShowGiftGrid"
          class="avatar-loading-dot"
        ></div>
        <div
          v-if="isSearchingGifts && !shouldShowGiftGrid"
          class="avatar-search-indicator"
        >
          <div class="avatar-search-spinner"></div>
          <p>Searching for gifts...</p>
          <small>Please wait while we find the perfect suggestions</small>
        </div>
      </div>
    </div>

    <!-- Gift grid — independent sibling, appears below the persistent avatar. -->
    <div v-if="shouldShowGiftGrid" class="new-layout-container">
      <!-- Gift Suggestions Section -->
      <div class="gift-suggestions-section">
        <div class="gift-suggestions-container">
          <div
            class="products-grid mt-4"
            :class="{ loading: isSearchingGifts }"
          >
            <div
              v-if="isSearchingGifts && shouldShowGiftGrid"
              class="gift-grid-loading-overlay"
            >
              <div class="gift-grid-loading-spinner"></div>
              <p>Loading gifts...</p>
            </div>

            <!-- No Results State -->
            <div
              v-else-if="
                shouldShowGiftGrid &&
                !isSearchingGifts &&
                (!giftSuggestions || giftSuggestions.length === 0)
              "
              class="no-results-container"
            >
              <div class="no-results-content">
                <div class="no-results-icon">
                  <i class="fas fa-gift"></i>
                </div>
                <h3 class="no-results-title">No Gift Suggestions Found</h3>
                <p class="no-results-message">
                  We couldn't find any gift suggestions at the moment. Try
                  asking the AI assistant for different gift ideas or check back
                  later.
                </p>
                <div class="no-results-actions">
                  <button class="retry-btn" @click="retryGiftSearch">
                    <i class="fas fa-redo"></i>
                    Try Again
                  </button>
                  <button class="clear-btn" @click="clearGiftsAndReturn">
                    <i class="fas fa-times"></i>
                    Clear Search
                  </button>
                </div>
              </div>
            </div>

            <div class="row" v-if="shouldShowGiftGrid">
              <img
                src="@/assets/site/images/loader.gif"
                v-if="isSearchingGifts"
                class="img-fluid loader-width"
                alt
              />
              <div
                class="col-md-3"
                v-for="(item, index) in getValidGiftItems()"
                v-if="!isSearchingGifts"
                :key="`${item.productId || index}-${
                  item.searchQuery || 'default'
                }`"
                :id="item.productId"
                :product-attribute="item.keywords"
                :class="{ 'new-item': item.isNew }"
              >
                <div
                  @click="openGiftLink(item.linkurl)"
                  class="owl-item active wizard-box"
                >
                  <div class="item product product-item">
                    <div class="item-inner clearfix">
                      <div class="box-image">
                        <a
                          class="product photo product-item-photo"
                          tabindex="-1"
                        >
                          <span
                            class="product-image-container product-image-container-2281"
                            style="width: 300px"
                          >
                            <span
                              class="product-image-wrapper"
                              style="padding-bottom: 100%"
                            >
                              <span v-if="item.imageurl">
                                <img
                                  class="wizard-image-photo lazyload"
                                  :src="item.imageurl"
                                  @error="() => (item.imageurl = null)"
                                  data-src=""
                                  loading="lazy"
                                  max-width="300"
                                  max-height="300"
                                  alt=""
                              /></span>
                              <span v-else>
                                <img
                                  class="wizard-image-photo lazyload"
                                  src="@/assets/site/images/noimage.jpeg"
                                  data-src=""
                                  loading="lazy"
                                  max-width="300"
                                  max-height="300"
                                  alt=""
                              /></span>
                            </span>
                          </span>
                        </a>
                        <div class="bottom-action my-3">
                          <a
                            data-post=""
                            @click.stop="savetoFavourite(item)"
                            class="action towishlist btn-action link-wishlist"
                            data-action="add-to-favourite"
                            title="Add to Favorites"
                          >
                            <span>Add to Favourite</span>
                          </a>
                          <a
                            data-post=""
                            @click.stop="selectOccasion(item)"
                            class="action tocart primary btn-action btn-cart"
                            data-action="add-to-wishlist"
                            title="Add to Wish List"
                          >
                            <span>Add to Wish List</span>
                          </a>
                          <a
                            class="action tocompare btn-action link-compare"
                            @click.stop="compareProduct(item)"
                            data-post=""
                            title="Add to Compare"
                          >
                            <span>Add to Compare</span>
                          </a>

                          <a
                            class="action quickview-handler sm_quickview_handler"
                            title="Quick View"
                            @click.stop="ViewProduct(item)"
                            ><span>Quick View</span></a
                          >
                        </div>
                      </div>
                      <div
                        class="product details product-item-details box-info"
                      >
                        <h2 class="product-item-name">
                          <a class="product-item-link">
                            {{ item.productname }}
                          </a>
                        </h2>

                        <div
                          class="price-box price-final_price"
                          data-role="priceBox"
                          data-product-id="2280"
                          data-price-box="product-id-2280"
                        >
                          <span
                            class="price-container price-final_price tax weee"
                          >
                            <span
                              id="product-price-2280"
                              data-price-amount="450"
                              data-price-type="finalPrice"
                              class="price-wrapper"
                              ><span class="price">
                                $ {{ item.price }}</span
                              ></span
                            >
                          </span>
                        </div>
                        <div class="product-reviews-summary short">
                          <div class="rating-summary">
                            <span class="label"><span>Rating:</span></span>
                            <div
                              class="rating-result"
                              id="rating-result_2280"
                              title="87%"
                            >
                              <span style="width: 87%"><span>87%</span></span>
                            </div>
                          </div>
                          <div class="reviews-actions">
                            <a class="action view">
                              &nbsp;<span>Review </span>
                            </a>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div
            class="gift-controls"
            v-if="
              shouldShowGiftGrid &&
              !isSearchingGifts &&
              getValidGiftItems().length > 0
            "
          >
            <div class="pagination-info">
              <span>Page {{ currentPage }} of {{ totalPages }}</span>
              <span class="item-count">
                ({{ getValidGiftItems().length }} items)
                <span
                  v-if="getValidGiftItems().length > 0"
                  class="accumulation-indicator"
                  title="Items are being accumulated from multiple searches"
                >
                  <i class="fas fa-layer-group"></i>
                </span>
              </span>
              <button
                @click="clearGiftsAndReturn"
                class="clear-all-btn"
                title="Clear all gift suggestions"
              >
                <i class="fas fa-trash"></i>
                Clear All
              </button>
            </div>
            <div class="pagination-controls">
              <button
                @click="previousPage"
                class="page-btn"
                :disabled="currentPage <= 1 || isSearchingGifts"
              >
                <i v-if="isSearchingGifts" class="fas fa-spinner fa-spin"></i>
                <i v-else class="fas fa-chevron-left"></i>
                Previous
              </button>
              <div class="page-numbers" v-if="totalPages > 1">
                <button
                  v-for="page in getPageNumbers()"
                  :key="page"
                  @click="goToPage(page)"
                  class="page-number-btn"
                  :class="{ active: page === currentPage }"
                  :disabled="isSearchingGifts"
                >
                  <span
                    v-if="isSearchingGifts && page === currentPage"
                    class="loading-dot"
                  ></span>
                  {{ page }}
                </button>
              </div>
              <button
                @click="nextPage"
                class="page-btn"
                :disabled="!hasNextPage || isSearchingGifts"
              >
                Next
                <i v-if="isSearchingGifts" class="fas fa-spinner fa-spin"></i>
                <i v-else class="fas fa-chevron-right"></i>
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
    <div>
      <occasion-modal v-on:savetoWishlist="savetoWishlist"></occasion-modal>
    </div>
  </div>
</template>

<script>
import {
  defineComponent,
  onBeforeUnmount,
  onMounted,
  watch,
  computed,
  ref,
  nextTick,
} from "@vue/composition-api";
import { useTavusConversation } from "../composables/useTavusConversation.js";
import { useGiftSearch } from "../composables/useGiftSearch.js";
import { storage } from "@/utils/storage.js";
import OccasionModal from "@/views/site/OccasionModal";
import { EventBus } from "@/eventBus"; // Import EventBus
import { requireLogin } from "@/utils/affiliatePurchase";
import {
  trackProductClick,
  trackProductEvent,
  getProductId,
} from "@/utils/userEvents";

export default defineComponent({
  name: "TavusAudioConversation",
  components: {
    OccasionModal,
  },
  data() {
    return {
      wishlist_product: null,
      errors: [],
      hasStarted: false, // Track if user has started the conversation
    };
  },
  setup() {
    // Use composables
    const {
      conversationData,
      conversationUrl,
      isConnected,
      connectionStatus,
      connectionStatusText,
      conversationTranscript,
      showTranscript,
      liveMessages,
      currentSpeaker,
      currentSpeech,
      isMuted,
      isSoundReduced,
      initializeConversation,
      setupIframe,
      toggleMute,
      reduceSound,
      disconnectConversation,
      clearError,
      displayTranscript,
      cleanup,
      updateStatus,
      updateVideoContainers,
    } = useTavusConversation();

    const {
      giftSuggestions,
      isSearchingGifts,
      currentPage,
      totalPages,
      hasNextPage,
      currentSearchQuery,
      showDefaultGiftPreview,
      itemOrderCounter,
      searchGifts,
      clearGiftSuggestions: rawClearGiftSuggestions,
      nextPage,
      previousPage,
      goToPage,
      getPageNumbers,
      openGiftLink,
    } = useGiftSearch();

    const giftsShouldPersistOnDisconnect = ref(false);
    const autoEndedForGifts = ref(false);

    const clearGiftSuggestions = () => {
      giftsShouldPersistOnDisconnect.value = false;
      autoEndedForGifts.value = false;
      rawClearGiftSuggestions();
    };

    // Track if user has started the conversation
    const hasStarted = ref(false);
    const isRestartingConversation = ref(false);
    const sessionId = ref(Date.now()); // Unique session ID for each conversation
    const initializationTimeout = ref(null);

    // Helper function to get valid gift items
    const getValidGiftItems = () => {
      const src = Array.isArray(giftSuggestions.value)
        ? giftSuggestions.value
        : [];
      const validItems = src.filter(
        (item) =>
          item &&
          (item.productId || item.product_id || item.id || item.sku) &&
          !item.error,
      );

      // Sort items to show the most recent API results at the top
      // Items with higher orderIndex (most recent) should appear first
      return validItems.sort((a, b) => {
        // If one item is marked as new and the other isn't, prioritize the new one
        if (a.isNew && !b.isNew) return -1;
        if (!a.isNew && b.isNew) return 1;

        // If both have the same new status, sort by orderIndex (most recent first)
        const orderA = a.orderIndex || 0;
        const orderB = b.orderIndex || 0;
        return orderB - orderA; // Higher orderIndex (newer items) appear first
      });
    };

    // Computed property to determine when to show gift grid
    const shouldShowGiftGrid = computed(() => {
      const validGiftItems = getValidGiftItems();
      const hasValidGifts = validGiftItems.length > 0;
      return hasValidGifts && !isSearchingGifts.value;
    });
    watch(
      shouldShowGiftGrid,
      (isVisible) => {
        EventBus.$emit("gift-grid-visibility", isVisible);
      },
      { immediate: true },
    );

    // Computed property to determine when to show the start button
    const showStartButton = computed(() => {
      // On 'error' the standalone error panel owns the screen, so the start
      // button must stay hidden or both would render at once. The Close
      // button on that panel clears the error and brings this back.
      if (connectionStatus.value === "error") return false;
      return (
        !hasStarted.value &&
        !isConnected.value &&
        connectionStatus.value !== "connecting"
      );
    });

    // Tracks whether to show the "please log in" panel instead of trying to
    // mint a session for an anonymous visitor.
    const needsLogin = ref(false);

    // Check whether a customer is currently logged in by inspecting localStorage.
    const isCustomerLoggedIn = () => {
      try {
        const raw = localStorage.getItem("userData");
        if (!raw) return false;
        const parsed = JSON.parse(raw);
        const token =
          (parsed && parsed.token) ||
          (parsed &&
            parsed.user &&
            (parsed.user.api_token || parsed.user.token)) ||
          (parsed && parsed.customer && parsed.customer.api_token);
        return !!token;
      } catch (e) {
        return false;
      }
    };

    const dismissLoginPrompt = () => {
      needsLogin.value = false;
    };

    // Dismiss a fatal-error panel: clear the message so connectionStatus
    // leaves 'error', and reset hasStarted so the start button returns.
    const dismissFatalError = () => {
      clearError();
      hasStarted.value = false;
    };
    const goToLogin = () => {
      needsLogin.value = false;
      const next = encodeURIComponent(
        window.location.pathname + window.location.search,
      );
      window.location.href = "/login?redirect=" + next;
    };

    // Cooldown management: 2 minutes 30 seconds = 150,000 ms
    const COOLDOWN_DURATION_MS = (2 * 60 + 30) * 1000;
    const isCooldownModalOpen = ref(false);
    const cooldownRemainingSeconds = ref(0);
    let cooldownTimerInterval = null;

    const formattedCooldownTime = computed(() => {
      const totalSec = cooldownRemainingSeconds.value;
      if (totalSec <= 0) return "0m 00s";
      const mins = Math.floor(totalSec / 60);
      const secs = totalSec % 60;
      return `${mins}m ${secs < 10 ? "0" : ""}${secs}s`;
    });

    const cooldownProgressPercent = computed(() => {
      const totalDurationSec = 150;
      const remaining = cooldownRemainingSeconds.value;
      const elapsed = Math.max(0, totalDurationSec - remaining);
      return Math.min(100, Math.max(0, (elapsed / totalDurationSec) * 100));
    });

    const openCooldownModal = (remainingMs) => {
      if (cooldownTimerInterval) {
        clearInterval(cooldownTimerInterval);
        cooldownTimerInterval = null;
      }
      cooldownRemainingSeconds.value = Math.max(
        0,
        Math.ceil(remainingMs / 1000),
      );
      isCooldownModalOpen.value = true;

      cooldownTimerInterval = setInterval(() => {
        const lastEnd = storage.getLastConversationEndTime();
        if (!lastEnd) {
          cooldownRemainingSeconds.value = 0;
          clearInterval(cooldownTimerInterval);
          cooldownTimerInterval = null;
          return;
        }
        const elapsed = Date.now() - lastEnd;
        const rem = Math.max(
          0,
          Math.ceil((COOLDOWN_DURATION_MS - elapsed) / 1000),
        );
        cooldownRemainingSeconds.value = rem;
        if (rem <= 0) {
          clearInterval(cooldownTimerInterval);
          cooldownTimerInterval = null;
        }
      }, 1000);
    };

    const closeCooldownModal = () => {
      isCooldownModalOpen.value = false;
      if (cooldownTimerInterval) {
        clearInterval(cooldownTimerInterval);
        cooldownTimerInterval = null;
      }
    };

    const startFromCooldown = () => {
      closeCooldownModal();
      startConversation();
    };

    // Method to start the conversation
    // Daily SDK is already imported via @daily-co/daily-js; skip loading call-machine bundle to avoid errors.
    const loadCallMachineScript = () => Promise.resolve();

    // Function to ensure video containers exist
    const ensureVideoContainers = () => {
      try {
        console.log("Ensuring video containers exist...");

        // Find the main video container
        let videoContainer = document.querySelector("#remote-videos-full");
        if (!videoContainer) {
          console.log("Video container not found, creating it...");
          const container = document.querySelector("#video-container");
          if (container) {
            videoContainer = document.createElement("div");
            videoContainer.id = "remote-videos-full";
            videoContainer.className = "avatar-video";
            container.appendChild(videoContainer);
            console.log("Created video container");
          } else {
            console.error("Video container parent not found");
            return false;
          }
        }

        // Clear any existing content
        while (videoContainer.firstChild) {
          videoContainer.removeChild(videoContainer.firstChild);
        }

        // Anam needs a <video id="anam-avatar-video"> inside the container
        // to stream into. Re-inject it after every reset.
        const anamVideo = document.createElement("video");
        anamVideo.id = "anam-avatar-video";
        anamVideo.autoplay = true;
        anamVideo.playsInline = true;
        anamVideo.muted = false;
        anamVideo.setAttribute("playsinline", "");
        videoContainer.appendChild(anamVideo);

        console.log("Video containers ensured");
        return true;
      } catch (error) {
        console.error("Error ensuring video containers:", error);
        return false;
      }
    };

    // Function to completely reset everything
    const completeReset = () => {
      try {
        console.log("Starting complete reset...");

        // Clear any initialization timeout
        if (initializationTimeout.value) {
          clearTimeout(initializationTimeout.value);
          initializationTimeout.value = null;
        }

        // Remove all iframes
        const iframes = document.querySelectorAll("iframe");
        iframes.forEach((iframe) => iframe.remove());

        // Remove all video and audio elements
        const mediaElements = document.querySelectorAll("video, audio");
        mediaElements.forEach((element) => {
          element.pause();
          element.src = "";
          element.load();
          element.remove();
        });

        // Clear all video containers
        const videoContainers = document.querySelectorAll(".avatar-video");
        videoContainers.forEach((container) => {
          while (container.firstChild) {
            container.removeChild(container.firstChild);
          }
        });

        // Clear Daily session data
        localStorage.removeItem("daily-session");
        sessionStorage.removeItem("daily-session");

        // Remove Daily objects to avoid duplicate DailyIframe instances
        if (window.Daily) {
          delete window.Daily;
        }
        if (window.DailyIframe) {
          delete window.DailyIframe;
        }

        // Clear conversation data
        storage.clearStoredConversation();
        conversationData.value = null;
        conversationUrl.value = null;

        console.log("Complete reset finished");
      } catch (error) {
        console.error("Error during complete reset:", error);
      }
    };

    const startConversation = async () => {
      if (
        connectionStatus.value === "connecting" ||
        isRestartingConversation.value
      )
        return;

      // Block anonymous visitors before we try to mint a session — gives
      // the user a friendly login CTA instead of a generic "no avatar"
      // failure they can't act on.
      if (!isCustomerLoggedIn()) {
        needsLogin.value = true;
        return;
      }

      // Check cooldown timer (2m 30s)
      const lastEnd = storage.getLastConversationEndTime();
      if (lastEnd) {
        const elapsed = Date.now() - lastEnd;
        if (elapsed >= 0 && elapsed < COOLDOWN_DURATION_MS) {
          openCooldownModal(COOLDOWN_DURATION_MS - elapsed);
          return;
        }
      }

      isRestartingConversation.value = true;
      hasStarted.value = true;
      clearGiftSuggestions();
      autoEndedForGifts.value = false;

      // Generate new session ID
      sessionId.value = Date.now();

      // Clear any error left over from a previous failed session BEFORE
      // pinning 'connecting'. connectionStatus checks error first, so a
      // stale message would otherwise keep the error panel on screen
      // through the whole new start.
      clearError();
      // Pin 'connecting' for the whole start. This is no longer a no-op:
      // it holds the status until the session reports a real state, so the
      // "Govava Avatar Disconnected" label can never flash mid-start.
      updateStatus("connecting", "Connecting...");

      // Emit event that chat is starting
      EventBus.$emit("chat-started");

      await nextTick();

      // Perform complete reset and ensure any previous call is fully cleaned
      await disconnectConversation();
      completeReset();

      // Ensure video containers exist
      const containersReady = ensureVideoContainers();
      if (!containersReady) {
        console.error("Failed to ensure video containers");
        hasStarted.value = false;
        updateStatus("error", "Setup Failed");
        isRestartingConversation.value = false;
        return;
      }

      try {
        console.log("Starting conversation with session ID:", sessionId.value);

        // Load the Daily script
        await loadCallMachineScript();

        // Set a timeout to handle initialization failure
        initializationTimeout.value = setTimeout(() => {
          if (connectionStatus.value === "connecting") {
            console.error("Conversation initialization timed out");
            hasStarted.value = false;
            updateStatus("error", "Connection Timeout");
            isRestartingConversation.value = false;
          }
        }, 30000); // 30 seconds timeout

        // Initialize the conversation
        await initializeConversation();

        // Clear the timeout if initialization succeeds
        if (initializationTimeout.value) {
          clearTimeout(initializationTimeout.value);
          initializationTimeout.value = null;
        }

        // Force update video containers after initialization
        setTimeout(() => {
          updateVideoContainers();
        }, 500);

        console.log("Conversation started successfully");
      } catch (error) {
        console.error("Failed to start conversation:", error);
        hasStarted.value = false;
        updateStatus("error", "Connection Failed");

        // Clear the timeout on error
        if (initializationTimeout.value) {
          clearTimeout(initializationTimeout.value);
          initializationTimeout.value = null;
        }
      } finally {
        isRestartingConversation.value = false;
      }
    };

    const endConversation = async (optionsOrEvent = {}) => {
      const hasKeepGiftsFlag =
        optionsOrEvent &&
        typeof optionsOrEvent === "object" &&
        Object.prototype.hasOwnProperty.call(optionsOrEvent, "keepGifts");
      const keepGifts = hasKeepGiftsFlag && optionsOrEvent.keepGifts === true;
      giftsShouldPersistOnDisconnect.value = keepGifts;

      try {
        console.log("Ending conversation...");

        // Clear any initialization timeout
        if (initializationTimeout.value) {
          clearTimeout(initializationTimeout.value);
          initializationTimeout.value = null;
        }

        // Disconnect the conversation
        await disconnectConversation();

        // Perform complete reset
        completeReset();

        storage.setLastConversationEndTime();
        console.log("Conversation ended successfully");
      } finally {
        storage.setLastConversationEndTime();
        hasStarted.value = false;
        isRestartingConversation.value = false;
        if (!keepGifts) {
          clearGiftSuggestions();
        }
        // Reset connection status to allow fresh start
        updateStatus("disconnected", "Disconnected");

        // Emit event that chat has ended
        EventBus.$emit("chat-ended");
      }
    };

    const leaveConversationKeepingGifts = () => {
      const keepGifts = shouldShowGiftGrid.value;
      return endConversation({ keepGifts });
    };

    // Listen for gift search events from Tavus conversation
    const handleGiftSearch = (event) => {
      console.log("🎁 Gift search event received:", event.detail);
      if (event.detail && event.detail.keywords) {
        console.log(
          "🔍 Triggering gift search with keywords:",
          event.detail.keywords,
        );
        searchGifts(event.detail.keywords, 1);
      }
    };

    // Listen for gift titles extracted from AI response
    const handleGiftTitlesExtracted = (event) => {
      console.log("🏷️ Gift titles extracted event received:", event.detail);
      const titles = event.detail.titles;

      // If we have specific gift titles, use them for more targeted search
      if (titles && titles.length > 0) {
        console.log("🎁 Using extracted gift titles for search:", titles);
        // Use the first few titles as search keywords
        const titleKeywords = titles.slice(0, 3).join(" ");
        searchGifts(titleKeywords, 1);
      }
    };

    onMounted(() => {
      // Add event listeners
      window.addEventListener("gift-search", handleGiftSearch);
      window.addEventListener(
        "gift-titles-extracted",
        handleGiftTitlesExtracted,
      );
    });

    // Watch for changes in gift suggestions to update video containers
    watch(giftSuggestions, (newGifts, oldGifts) => {
      const newLen = Array.isArray(newGifts) ? newGifts.length : 0;
      const oldLen = Array.isArray(oldGifts) ? oldGifts.length : 0;
      // If gifts were added or removed, update video containers
      if (newLen !== oldLen) {
        // Use nextTick to ensure DOM is updated
        setTimeout(() => {
          updateVideoContainers();
        }, 100);
      }
    });

    // Watch for changes in shouldShowGiftGrid to handle transitions
    watch(shouldShowGiftGrid, (shouldShow, wasShowing) => {
      if (shouldShow && !wasShowing) {
        // Add a small delay to ensure smooth transition
        setTimeout(() => {
          updateVideoContainers();
        }, 300);
      } else if (!shouldShow && wasShowing) {
        // Update video containers when returning to centered view
        setTimeout(() => {
          updateVideoContainers();
        }, 100);
      }
    });

    // REMOVED: legacy watcher that auto-closed the session the moment the
    // gift grid populated. That was a race against the model's STEP 10/13
    // tool-call flow — the model would fire search_gifts, the grid would
    // render, this watcher would fire endConversation before the model
    // ever got to ask "Are you satisfied?" or call end_conversation. Now
    // the session only ends when (a) the model fires end_conversation, or
    // (b) the user clicks the explicit Leave button.
    // (Original code kept here as a comment so we know what was removed.)
    //   watch([shouldShowGiftGrid, isConnected], ([showGrid, connected]) => {
    //     if (showGrid && connected && !autoEndedForGifts.value) {
    //       autoEndedForGifts.value = true;
    //       endConversation({ keepGifts: true });
    //     } else if (!showGrid) {
    //       autoEndedForGifts.value = false;
    //     }
    //   });

    watch(connectionStatus, (status) => {
      console.log("Connection status changed to:", status);
      // On 'error' the panel must STAY mounted: that is the only place the
      // failure message is rendered. Clearing hasStarted here flipped
      // showStartButton back on, unmounted the whole avatar card and threw
      // the message away with it — the user just saw the start button again
      // with no idea why the session died. Only a clean 'disconnected'
      // returns to the start button.
      if (!isRestartingConversation.value && status === "error") {
        return;
      }
      if (!isRestartingConversation.value && status === "disconnected") {
        hasStarted.value = false;
        // Always keep gifts on disconnect. Whether the close came from the
        // model firing end_conversation, the user clicking Leave, or a
        // network drop, the user wants to keep browsing the products that
        // got pulled up. Only an explicit "new session" path clears them
        // (see startConversation → clearGiftSuggestions, line 581).
        // (Removed: `if (!giftsShouldPersistOnDisconnect.value) clearGiftSuggestions();`
        //  which dropped the products on every model-initiated close because
        //  the composable's runEndConversation never sets that flag.)
      }
    });

    onBeforeUnmount(() => {
      if (cooldownTimerInterval) {
        clearInterval(cooldownTimerInterval);
        cooldownTimerInterval = null;
      }

      // Remove event listeners
      window.removeEventListener("gift-search", handleGiftSearch);
      window.removeEventListener(
        "gift-titles-extracted",
        handleGiftTitlesExtracted,
      );

      // Cleanup conversation
      cleanup();

      // Clear any initialization timeout
      if (initializationTimeout.value) {
        clearTimeout(initializationTimeout.value);
      }

      // Perform complete reset
      clearGiftSuggestions();
      completeReset();
    });

    return {
      // Tavus conversation state
      conversationData,
      conversationUrl,
      isConnected,
      connectionStatus,
      connectionStatusText,
      conversationTranscript,
      showTranscript,
      liveMessages,
      currentSpeaker,
      currentSpeech,
      isMuted,
      isSoundReduced,

      // Gift search state
      giftSuggestions,
      isSearchingGifts,
      currentPage,
      totalPages,
      hasNextPage,
      currentSearchQuery,
      showDefaultGiftPreview,
      itemOrderCounter,

      // Tavus conversation methods
      toggleMute,
      endConversation,
      leaveConversationKeepingGifts,
      reduceSound,

      // Gift search methods
      searchGifts,
      clearGiftSuggestions,
      nextPage,
      previousPage,
      goToPage,
      getPageNumbers,
      openGiftLink,

      // Computed properties
      shouldShowGiftGrid,
      showStartButton,

      // Helper functions
      getValidGiftItems,

      // New method
      startConversation,

      // Login-required CTA
      needsLogin,
      dismissLoginPrompt,
      dismissFatalError,
      goToLogin,

      // Cooldown dialog state & handlers
      isCooldownModalOpen,
      cooldownRemainingSeconds,
      formattedCooldownTime,
      cooldownProgressPercent,
      closeCooldownModal,
      startFromCooldown,
    };
  }, // end setup
  methods: {
    savetoFavourite(product) {
      if (!requireLogin()) {
        return;
      }

      var token = localStorage.getItem("userData");
      var userdata_array = JSON.parse(token);
      var api_token = userdata_array.user.api_token;
      var user_id = userdata_array.user.id;
      var fetch_url = process.env.VUE_APP_URL + "customer/favourite";
      fetch(fetch_url, {
        method: "post",
        headers: {
          "content-type": "application/json",
          "X-Requested-With": "XMLHttpRequest",
          Authorization: "Bearer " + api_token,
        },
        body: JSON.stringify({
          product_id: product.productId,
          product: product,
          user_id: user_id,
          clickstream_data: null,
          user_action: "AddToFav",
        }),
      })
        .then((res) => res.json())
        .then((data) => {
          if (data.status === true) {
            this.$swal.fire("Good to Go", data.message, "success");
          } else if (data.status === false) {
            this.$swal.fire("Error", data.message, "error");
          } else {
            this.errors = data.errors;
          }
        })
        .catch(function (err) {
          this.$swal("Product", "Error : " + err.message, "error");
        });
    },
    selectOccasion(product) {
      if (!requireLogin()) {
        return;
      }

      this.wishlist_product = product;
      this.$bvModal.show("occasion-modal");
    },
    compareProduct(product) {
      if (!requireLogin()) {
        return;
      }

      var token = localStorage.getItem("userData");
      var userdata_array = JSON.parse(token);
      var api_token = userdata_array.token;
      var user_id = userdata_array.user.id;
      var fetch_url = process.env.VUE_APP_URL + "customer/compare";
      fetch(fetch_url, {
        method: "post",
        headers: {
          "content-type": "application/json",
          "X-Requested-With": "XMLHttpRequest",
          Authorization: "Bearer " + api_token,
        },
        body: JSON.stringify({
          product_id: product.productId,
          product: product,
          user_id: user_id,
        }),
      })
        .then((res) => res.json())
        .then((data) => {
          if (data.status === true) {
            this.$swal.fire("Good to Go", data.message, "success");
          } else if (data.status === false) {
            this.$swal.fire("Error", data.message, "error");
          } else {
            this.errors = data.errors;
          }
        })
        .catch(function (err) {
          this.$swal("Product", "Error : " + err.message, "error");
        });
    },
    ViewProduct(product) {
      trackProductClick(product, "ai_gift");
      this.$store.state.pet_id = null;
      const id = Math.random().toString(36).slice(2);
      this.$router.push({
        path: "/single-product/product/" + id,
        query: { singleproduct: product },
      });
    },
    savetoWishlist(occasion) {
      //Save or update User Details
      this.$bvModal.hide("occasion-modal");
      var token = localStorage.getItem("userData");
      var userdata_array = JSON.parse(token);
      var api_token = userdata_array.token;
      var user_id = userdata_array.user.id;
      var fetch_url = process.env.VUE_APP_URL + "customer/userwishlist";
      fetch(fetch_url, {
        method: "post",
        headers: {
          "content-type": "application/json",
          "X-Requested-With": "XMLHttpRequest",
          Authorization: "Bearer " + api_token,
        },
        body: JSON.stringify({
          product_id: this.wishlist_product.productId,
          product: this.wishlist_product,
          user_id: user_id,
          wishlist_type: "User",
          occasion_id: occasion.id,
          user_action: "AddToWishlist",
          clickstream_data: null,
        }),
      })
        .then((res) => res.json())
        .then((data) => {
          if (data.status === true) {
            this.$swal.fire("Good to Go", data.message, "success");
          } else if (data.status === false) {
            this.$swal.fire("Error", data.message, "error");
          } else {
            this.errors = data.errors;
          }
        })
        .catch(function (err) {
          this.$swal("Product", "Error : " + err.message, "error");
        });

      var fetch_url =
        process.env.VUE_APP_URL +
        "customer/products/saveAffilativeNetwork/" +
        this.wishlist_product.productId;
      fetch(fetch_url, {
        method: "get",
        headers: {
          "content-type": "application/json",
          "X-Requested-With": "XMLHttpRequest",
          Authorization: "Bearer " + api_token,
        },
      })
        .then((res) => res.json())
        .then((data) => {});

      this.wishlist_product = null;
    },
    retryGiftSearch() {
      // Retry the last search query if available
      if (this.currentSearchQuery) {
        this.searchGifts(this.currentSearchQuery, 1, true); // Accumulate items
      } else {
        // If no previous query, trigger a new search with default keywords
        this.searchGifts("gift suggestions", 1, true); // Accumulate items
      }
    },

    // Method to start a fresh search (not accumulate)
    startFreshSearch(searchText) {
      this.searchGifts(searchText, 1, false); // Don't accumulate
    },

    // Method to clear gifts and return to centered avatar
    clearGiftsAndReturn() {
      this.clearGiftSuggestions();
    },
  },
});
</script>

<style scoped>
/* When the call has ended but the avatar card is still rendered to preserve
   layout, click-through it so anything underneath remains clickable. */
.avatar-card-inactive {
  pointer-events: none;
}
.avatar-card-inactive .avatar-controls,
.avatar-card-inactive .avatar-controls-bar {
  display: none;
}

/* ---- Login-required modal (shown when anonymous visitor clicks the chat trigger) ---- */
.govava-login-modal {
  position: fixed;
  inset: 0;
  background: rgba(15, 19, 32, 0.55);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 99999;
  padding: 16px;
  animation: govava-fade 180ms ease-out;
}
@keyframes govava-fade {
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
}
.govava-login-card {
  background: #fff;
  border-radius: 16px;
  max-width: 420px;
  width: 100%;
  padding: 28px 28px 24px;
  text-align: center;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.25);
  position: relative;
}
.govava-login-close {
  position: absolute;
  top: 8px;
  right: 14px;
  background: transparent;
  border: 0;
  font-size: 28px;
  line-height: 1;
  color: #888;
  cursor: pointer;
  padding: 4px 8px;
}
.govava-login-close:hover {
  color: #222;
}
.govava-login-icon img {
  width: 96px;
  height: 96px;
  border-radius: 50%;
  object-fit: cover;
  margin-bottom: 14px;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.12);
}
.govava-login-title {
  font-size: 22px;
  font-weight: 700;
  margin: 0 0 8px;
  color: #1a1a1a;
}
.govava-login-sub {
  font-size: 14.5px;
  color: #555;
  line-height: 1.55;
  margin: 0 0 20px;
}
.govava-login-actions {
  display: flex;
  flex-direction: column;
  gap: 10px;
}
.govava-login-primary {
  background: #2c7be5;
  color: #fff;
  border: 0;
  padding: 12px 18px;
  border-radius: 10px;
  font-size: 15px;
  font-weight: 600;
  cursor: pointer;
  transition: background 120ms;
}
.govava-login-primary:hover {
  background: #1f5bb8;
}
.govava-login-secondary {
  background: transparent;
  color: #666;
  border: 0;
  padding: 8px 12px;
  font-size: 13.5px;
  cursor: pointer;
  text-decoration: underline;
}
.govava-login-secondary:hover {
  color: #222;
}
.govava-login-meta {
  font-size: 12px;
  color: #888;
  margin: 14px 0 0;
}

/* ---- Cooldown modal (shown when user clicks chat trigger before 2m 30s cooldown expires) ---- */
.govava-cooldown-modal {
  position: fixed;
  inset: 0;
  background: rgba(15, 23, 42, 0.72);
  backdrop-filter: blur(8px);
  -webkit-backdrop-filter: blur(8px);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 999999;
  padding: 16px;
  animation: govava-cooldown-fade 200ms ease-out;
}
@keyframes govava-cooldown-fade {
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
}

.govava-cooldown-card {
  background: #ffffff;
  border-radius: 24px;
  max-width: 440px;
  width: 100%;
  padding: 34px 28px 28px;
  text-align: center;
  box-shadow: 0 25px 65px -12px rgba(0, 0, 0, 0.35),
    0 0 0 1px rgba(226, 232, 240, 0.8);
  position: relative;
  animation: govava-cooldown-slide 240ms cubic-bezier(0.16, 1, 0.3, 1);
}
@keyframes govava-cooldown-slide {
  from {
    transform: scale(0.95) translateY(10px);
    opacity: 0;
  }
  to {
    transform: scale(1) translateY(0);
    opacity: 1;
  }
}

.govava-cooldown-close {
  position: absolute;
  top: 14px;
  right: 14px;
  background: #f1f5f9 !important;
  border: none !important;
  color: #64748b !important;
  width: 34px;
  height: 34px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  padding: 0 !important;
  transition: all 180ms ease;
}
.govava-cooldown-close:hover {
  background: #e2e8f0 !important;
  color: #0f172a !important;
  transform: rotate(90deg);
}

.govava-cooldown-icon {
  position: relative;
  display: inline-block;
  margin-bottom: 16px;
}
.govava-cooldown-avatar-ring {
  width: 96px;
  height: 96px;
  border-radius: 50%;
  padding: 3px;
  background: linear-gradient(135deg, #3b82f6, #6366f1, #f43f5e);
  display: inline-flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 8px 25px rgba(59, 130, 246, 0.28);
}
.govava-cooldown-avatar-ring img {
  width: 100%;
  height: 100%;
  border-radius: 50%;
  object-fit: cover;
  border: 2.5px solid #ffffff;
}

.govava-cooldown-badge {
  position: absolute;
  bottom: -2px;
  right: -2px;
  background: linear-gradient(135deg, #ff6b6b, #ee5253);
  color: #ffffff !important;
  width: 32px;
  height: 32px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 4px 12px rgba(238, 82, 83, 0.45);
  border: 2.5px solid #ffffff;
  animation: pulse-cooldown-badge 2s infinite ease-in-out;
}
.govava-cooldown-badge.badge-ready {
  background: linear-gradient(135deg, #10b981, #059669);
  box-shadow: 0 4px 12px rgba(16, 185, 129, 0.45);
}
@keyframes pulse-cooldown-badge {
  0%,
  100% {
    transform: scale(1);
  }
  50% {
    transform: scale(1.08);
  }
}

.govava-cooldown-title {
  font-size: 23px;
  font-weight: 800;
  margin: 0 0 10px;
  color: #0f172a;
  letter-spacing: -0.5px;
  line-height: 1.25;
}
.govava-cooldown-sub {
  font-size: 14px;
  color: #64748b;
  line-height: 1.6;
  margin: 0 0 22px;
  padding: 0 8px;
}

/* Timer Box */
.govava-cooldown-timer-box {
  background: linear-gradient(180deg, #f8fafc 0%, #f1f5f9 100%);
  border: 1.5px solid #e2e8f0;
  border-radius: 18px;
  padding: 18px 20px;
  margin-bottom: 24px;
  box-shadow: inset 0 1px 2px rgba(0, 0, 0, 0.03);
}
.govava-cooldown-timer-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 10px;
}
.govava-cooldown-timer-label {
  font-size: 11px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.8px;
  color: #64748b;
}
.govava-cooldown-timer-status {
  background: #fee2e2;
  color: #ef4444;
  padding: 4px 10px;
  border-radius: 999px;
  font-size: 11.5px;
  font-weight: 700;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  border: 1px solid #fecaca;
}
.govava-cooldown-timer-status.ready {
  background: #dcfce7;
  color: #16a34a;
  border: 1px solid #bbf7d0;
}
.govava-cooldown-pulse-dot {
  width: 7px;
  height: 7px;
  background-color: #ef4444;
  border-radius: 50%;
  display: inline-block;
  animation: pulse-dot 1.2s infinite ease-in-out;
}
.govava-cooldown-ready-dot {
  width: 7px;
  height: 7px;
  background-color: #16a34a;
  border-radius: 50%;
  display: inline-block;
}

.govava-cooldown-timer-display {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  color: #0284c7;
  margin: 10px 0 14px;
}
.govava-cooldown-timer-display.display-ready {
  color: #10b981;
}
.cooldown-clock-icon {
  stroke: #0284c7;
  animation: tick-tock 2s infinite ease-in-out;
}
@keyframes tick-tock {
  0%,
  100% {
    transform: rotate(0deg);
  }
  25% {
    transform: rotate(6deg);
  }
  75% {
    transform: rotate(-6deg);
  }
}
.govava-cooldown-timer-digits {
  font-size: 38px;
  font-weight: 800;
  letter-spacing: -0.5px;
  font-variant-numeric: tabular-nums;
  line-height: 1;
}

.govava-cooldown-progress-bar {
  background: #e2e8f0;
  height: 7px;
  border-radius: 999px;
  overflow: hidden;
}
.govava-cooldown-progress-fill {
  background: linear-gradient(90deg, #0284c7, #38bdf8);
  height: 100%;
  border-radius: 999px;
  transition: width 1s linear;
  box-shadow: 0 0 10px rgba(2, 132, 199, 0.4);
}
.govava-cooldown-progress-fill.fill-ready {
  background: linear-gradient(90deg, #10b981, #34d399);
  box-shadow: 0 0 10px rgba(16, 185, 129, 0.4);
}

/* Action Buttons */
.govava-cooldown-actions {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.govava-cooldown-btn {
  display: inline-flex !important;
  align-items: center !important;
  justify-content: center !important;
  width: 100% !important;
  padding: 14px 24px !important;
  border-radius: 14px !important;
  font-size: 15px !important;
  font-weight: 700 !important;
  letter-spacing: 0.2px !important;
  border: none !important;
  cursor: pointer !important;
  transition: all 180ms ease !important;
  text-decoration: none !important;
}

/* Waiting Button (Solid modern slate - distinctly active and high contrast!) */
.govava-btn-waiting {
  background: #0f172a !important;
  color: #ffffff !important;
  box-shadow: 0 4px 14px rgba(15, 23, 42, 0.28) !important;
}
.govava-btn-waiting:hover {
  background: #1e293b !important;
  color: #ffffff !important;
  box-shadow: 0 6px 20px rgba(15, 23, 42, 0.38) !important;
  transform: translateY(-1px);
}
.govava-btn-waiting:active {
  transform: translateY(0);
}

/* Ready Button (Vibrant Emerald Green) */
.govava-btn-ready {
  background: linear-gradient(135deg, #10b981, #059669) !important;
  color: #ffffff !important;
  box-shadow: 0 6px 20px rgba(16, 185, 129, 0.35) !important;
}
.govava-btn-ready:hover {
  background: linear-gradient(135deg, #059669, #047857) !important;
  color: #ffffff !important;
  box-shadow: 0 8px 24px rgba(16, 185, 129, 0.45) !important;
  transform: translateY(-1px);
}
.govava-btn-ready:active {
  transform: translateY(0);
}

/* CSS Variables */
:root {
  --primary-bg: #ffffff;
  --secondary-bg: #f8f9fa;
  --tertiary-bg: #e9ecef;
  --text-primary: #212529;
  --text-secondary: #6c757d;
  --accent-red: #dc3545;
  --accent-yellow: #ffc107;
  --accent-green: #28a745;
  --border-color: #dee2e6;
  --primary-color: #667eea;
}

/* Reset and base styles */
* {
  margin: 0;
  padding: 0;
  box-sizing: border-box;
}

body {
  font-family: -apple-system, BlinkMacSystemFont, Segoe UI, Roboto,
    Helvetica Neue, Arial, sans-serif, Apple Color Emoji, Segoe UI Emoji,
    Segoe UI Symbol;
  color: var(--text-primary);
  line-height: 1.6;
  -webkit-font-smoothing: antialiased;
  -moz-osx-font-smoothing: grayscale;
  margin: 0;
  padding: 0;
  min-height: 100vh;
}

/* Button styles */
button {
  font-family: inherit;
  border: none;
  outline: none;
  cursor: pointer;
  transition: all 0.2s ease;
}

button:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

/* Video element styles */
video {
  background-color: var(--primary-bg);
  border-radius: 8px;
  max-width: 100%;
  max-height: 100%;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.15);
  object-fit: cover;
  border-radius: 0px !important;
}

/* Animation keyframes */
@keyframes pulse {
  0% {
    opacity: 1;
  }
  50% {
    opacity: 0.5;
  }
  100% {
    opacity: 1;
  }
}

@keyframes spin {
  0% {
    transform: rotate(0deg);
  }
  100% {
    transform: rotate(360deg);
  }
}

@keyframes fadeIn {
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

/* Main Container */
.tavus-audio-conversation {
  width: 100%;
  display: flex;
  flex-direction: column;
  margin: 0;
  padding: 0;
  position: relative;
}

.tavus-audio-conversation.has-start-trigger {
  padding-top: 0;
}

/* Start Chat Trigger */
.start-chat-trigger {
  position: fixed;
  top: 320px;
  right: 24px;
  z-index: 1000;
  pointer-events: none;
}

.start-chat-trigger-btn {
  display: flex;
  align-items: center;
  gap: 14px;
  border-radius: 999px;
  border: none;
  background: transparent;
  padding: 0;
  box-shadow: none;
  cursor: pointer;
  transition: transform 0.2s ease;
  pointer-events: auto;
}

.start-chat-trigger-btn,
.start-chat-trigger-btn:hover,
.start-chat-trigger-btn:focus {
  background-color: transparent !important;
  box-shadow: none !important;
}

.start-chat-trigger-gif {
  width: 110px;
  height: 110px;
  border-radius: 20px;
  object-fit: cover;
  flex-shrink: 0;
}

.start-chat-trigger-text {
  font-size: 18px;
  font-weight: 700;
  color: #333;
  text-align: left;
  background: transparent;
  padding: 0;
}

.start-chat-trigger-btn span,
.start-chat-trigger-btn:hover span,
.start-chat-trigger-btn:focus span {
  background-color: transparent !important;
  color: #333 !important;
}

.start-chat-trigger-btn:hover .start-chat-trigger-text {
  color: #333;
}

@media (max-width: 768px) {
  .start-chat-trigger {
    right: 12px;
  }

  .start-chat-trigger-btn {
    justify-content: center;
  }

  .start-chat-trigger-gif {
    width: 90px;
    height: 90px;
  }
}

@media (max-width: 1279px) {
  .start-chat-trigger-text {
    display: none;
  }
  .start-chat-trigger-text {
    font-size: 16px;
  }
}

/* Transition effects */
.centered-avatar-container,
.main-content {
  transition: opacity 0.3s ease-in-out, transform 0.3s ease-in-out;
}

.centered-avatar-container {
  opacity: 1;
  transform: scale(1);
}

.main-content {
  opacity: 1;
  transform: scale(1);
}

/* Layout Classes */
.row {
  display: flex;
  flex-direction: row;
  width: 100%;
}

.col-md-9 {
  width: 75%;
}

.col-md-3 {
  width: 25%;
}

.main-content {
  display: flex;
  flex-direction: column;
  width: 100%;
  overflow: hidden;
}

.center-panel {
  flex: 1;
  display: flex;
  height: 100%;
  overflow-y: auto;
}

/* Centered Avatar Container */
.centered-avatar-container {
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 370px;
  width: 100%;
}

.centered-avatar-container.chat-hidden {
  opacity: 0;
  pointer-events: none;
  height: 0;
  min-height: 0;
  margin: 0;
  padding: 0;
  overflow: hidden;
}

/* When the gift grid is showing, dock the avatar as a floating window
   in the top-right corner — about half the centered footprint. The inner
   .avatar-medium keeps its original layout logic; we only reposition the
   parent and constrain its width. */
.centered-avatar-container.with-grid {
  position: fixed;
  top: 84px;
  right: 24px;
  width: 240px;
  min-height: 0;
  z-index: 1000;
  background: #fff;
  border-radius: 18px;
  box-shadow: 0 10px 28px rgba(0, 0, 0, 0.18), 0 2px 6px rgba(0, 0, 0, 0.08);
  padding: 8px;
}

.centered-avatar-container.with-grid .avatar-medium {
  width: 100%;
  max-width: 100%;
  min-height: 215px;
}

@media (max-width: 768px) {
  .centered-avatar-container.with-grid {
    top: 70px;
    right: 12px;
    width: 170px;
    padding: 6px;
  }
  .centered-avatar-container.with-grid .avatar-medium {
    min-height: 160px;
  }
}

@media (max-width: 480px) {
  .centered-avatar-container.with-grid {
    width: 130px;
  }
  .centered-avatar-container.with-grid .avatar-medium {
    min-height: 130px;
  }
}

/* Avatar Card Styles */
.avatar-card {
  /* background: #f8f9fa; */
  /* border-radius: 8px; */
  /* box-shadow: 0 2px 8px rgba(0,0,0,0.08); */
  /* border: 1.5px solid #e0e0e0; */
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 8px;
  position: relative;
}

.avatar-large {
  width: 30%;
  min-height: 370px;
  max-width: 98vw;
}

/* Fatal-error panel: the video frame is hidden, so drop the reserved
   avatar height and let the card shrink to just the message + button. */
.avatar-error-state.avatar-large,
.avatar-error-state.avatar-medium {
  min-height: 0;
  padding: 28px 16px;
}

.avatar-medium {
  width: 30%;
  max-width: 600px;
  min-height: 300px;
}

.avatar-small {
  width: 100%;
  margin: 0 auto;
}

.avatar-video {
  width: 100%;
  /* background: #f8f8f8; */
  /* border-radius: 12px 12px 0 0; */
  border-radius: 20px;
  margin: 0 auto;
  position: relative;
  overflow: hidden;
}

.avatar-video video {
  width: 100%;
  height: 100%;
  object-fit: cover;
  border-radius: 12px 12px 0 0;
}

.avatar-video audio {
  display: none;
}

/* Avatar Loading States */
.avatar-loading-indicator {
  margin-top: 8px;
  position: absolute;
  left: 50%;
  bottom: 300px;
  transform: translateX(-50%);
  z-index: 2;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: 38px;
  pointer-events: none;
}

.avatar-loading-spinner {
  width: 38px;
  height: 38px;
  left: 50%;
  bottom: 300px;
  border: 4px solid #f3f3f3;
  border-top: 4px solid #667eea;
  border-radius: 50%;
  animation: spin 1s linear infinite;
}

.avatar-loading-dot {
  margin-top: 8px;
  width: 16px;
  height: 16px;
  background: #667eea;
  border-radius: 50%;
  animation: pulse 1.2s infinite;
  box-shadow: 0 0 8px #667eea44;
}

.avatar-search-indicator {
  position: absolute;
  bottom: 80px;
  left: 50%;
  transform: translateX(-50%);
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
  background: rgba(255, 255, 255, 0.95);
  padding: 12px 16px;
  border-radius: 8px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  z-index: 10;
}

.avatar-search-spinner {
  width: 24px;
  height: 24px;
  border: 3px solid #f3f3f3;
  border-top: 3px solid #667eea;
  border-radius: 50%;
  animation: spin 1s linear infinite;
}

.avatar-search-indicator p {
  margin: 0;
  font-size: 14px;
  font-weight: 500;
  color: #667eea;
}

.avatar-search-indicator small {
  margin: 0;
  font-size: 11px;
  color: #666;
  opacity: 0.8;
}

.avatar-loading-text {
  color: #666;
  font-size: 14px;
  font-weight: 500;
  text-align: center;
  padding: 0 16px;
  line-height: 1.5;
  max-width: 320px;
}

.avatar-error-retry {
  margin-top: 12px;
  padding: 7px 20px;
  font-size: 13px;
  font-weight: 600;
  color: #fff;
  background: #e53935;
  border: none;
  border-radius: 6px;
  cursor: pointer;
}

.avatar-error-retry:hover {
  background: #c62828;
}

/* Avatar Controls */
.avatar-controls-bar {
  width: 100%;
  height: 1px;
  background: #232c34;
  border-radius: 0 0 0 0;
  margin: 0;
  margin-bottom: 0;
  display: flex;
  align-items: center;
  justify-content: flex-start;
}

.avatar-controls {
  width: 100%;
  display: flex;
  justify-content: space-around;
  align-items: center;
  /* padding-top: 8px; */
  margin-bottom: 0;
  background: #fff;
  background: #f8f9fa;
  border-radius: 8px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
  border: 1.5px solid #e0e0e0;
}

.avatar-control-btn {
  height: auto;
  background: none;
  border: none !important;
  color: #232c33 !important;
  display: flex;
  line-height: 1;
  flex-direction: column;
  align-items: center;
  font-size: 15px !important;
  font-weight: 500;
  cursor: pointer;
  padding: 12px !important;
  transition: color 0.2s;
}

.avatar-control-btn i {
  font-size: 18px;
  margin-bottom: 2px;
}

.avatar-control-btn span {
  font-size: 13px;
  font-weight: 400;
  margin-top: 0px;
}

.avatar-control-btn:hover {
  color: #667eea;
}

.leave-btn-outline {
  background: #fff;
  color: #ff2d2d !important;
  border-radius: 8px;
  width: 48px;
  height: 48px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  font-size: 22px;
  box-shadow: none;
  margin-left: 8px;
  margin-right: 0;
  padding: 0;
  transition: background 0.2s, color 0.2s, border 0.2s;
}

.leave-btn-outline i {
  color: #ff2d2d;
  margin-bottom: 2px;
  font-size: 22px;
}

.leave-btn-outline span {
  display: block;
  color: #222;
  font-size: 13px;
  font-weight: 400;
  margin-top: 2px;
}

.leave-btn-outline:hover {
  background: #ffeded;
}

/* New Layout Styles */
.new-layout-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 100%;
  padding: 20px;
}

.centered-avatar-section {
  display: flex;
  justify-content: center;
  width: 100%;
  margin-bottom: 30px;
}

.gift-suggestions-section {
  width: 100%;
  max-width: 1200px;
  margin: 0 auto;
}

.gift-suggestions-container {
  background: white;
  border-radius: 16px;
  padding: 24px;
  border: 1px solid rgba(0, 0, 0, 0.05);
  width: 100%;
}

/* Loading overlay for products grid */
.gift-grid-loading-overlay {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(255, 255, 255, 0.9);
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  z-index: 10;
  border-radius: 12px;
}

.gift-grid-loading-spinner {
  width: 40px;
  height: 40px;
  border: 4px solid #f3f3f3;
  border-top: 4px solid #667eea;
  border-radius: 50%;
  animation: spin 1s linear infinite;
  margin-bottom: 16px;
}

.gift-grid-loading-overlay p {
  color: #667eea;
  font-weight: 500;
  margin: 0;
}

/* Gift Controls */
.gift-controls {
  border-top: 1px solid #e9ecef;
  padding-top: 8px;
}

.pagination-info {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 6px;
  font-size: 11px;
  color: #666;
}

#video-container {
  min-height: 215px;
}

.clear-all-btn {
  background: #f8f9fa;
  color: #dc3545;
  border: 1px solid #dee2e6;
  padding: 4px 8px;
  border-radius: 4px;
  font-size: 11px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  gap: 4px;
}

.clear-all-btn:hover {
  background: #dc3545;
  color: white;
  border-color: #dc3545;
}

.clear-all-btn i {
  font-size: 10px;
}

.item-count {
  color: #999;
  display: flex;
  align-items: center;
  gap: 4px;
}

.accumulation-indicator {
  color: #667eea;
  font-size: 10px;
  opacity: 0.8;
}

.accumulation-indicator i {
  font-size: 8px;
}

.pagination-controls {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 12px;
  margin: 20px 0;
  flex-wrap: wrap;
}

.page-btn {
  background: #f8f9fa;
  color: #333;
  border: 1px solid #dee2e6;
  padding: 8px 16px;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 500;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  gap: 6px;
}

.page-btn:hover:not(:disabled) {
  background: #667eea;
  color: white;
  border-color: #667eea;
  transform: translateY(-1px);
}

.page-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
  transform: none;
}

.page-numbers {
  display: flex;
  gap: 4px;
}

.page-number-btn {
  width: 36px;
  height: 36px;
  border: 1px solid #dee2e6;
  background: white;
  color: #333;
  border-radius: 6px;
  font-size: 14px;
  font-weight: 500;
  transition: all 0.2s ease;
}

.page-number-btn:hover {
  background: #f8f9fa;
  border-color: #667eea;
}

.page-number-btn.active {
  background: #667eea;
  color: white;
  border-color: #667eea;
}

.page-number-btn:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.page-number-btn:disabled:hover {
  background: #f8f9fa;
  color: #6c757d;
}

.loading-dot {
  display: inline-block;
  width: 8px;
  height: 8px;
  background: #667eea;
  border-radius: 50%;
  animation: pulse 1.2s infinite;
  margin-right: 4px;
}

.page-btn .fa-spinner {
  margin-right: 4px;
}

.page-btn:last-child .fa-spinner {
  margin-right: 0;
  margin-left: 4px;
}

/* No Results State */
.no-results-container {
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 400px;
  padding: 40px 20px;
}

.no-results-content {
  text-align: center;
  max-width: 500px;
}

.no-results-icon {
  font-size: 64px;
  color: #667eea;
  margin-bottom: 24px;
  opacity: 0.7;
}

.no-results-title {
  font-size: 24px;
  font-weight: 600;
  color: #333;
  margin-bottom: 16px;
}

.no-results-message {
  font-size: 16px;
  color: #666;
  line-height: 1.6;
  margin-bottom: 32px;
}

.no-results-actions {
  display: flex;
  gap: 16px;
  justify-content: center;
  flex-wrap: wrap;
}

.retry-btn,
.clear-btn {
  padding: 12px 24px;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 500;
  border: none;
  cursor: pointer;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  gap: 8px;
}

.retry-btn {
  background: #667eea;
  color: white;
}

.retry-btn:hover {
  background: #5a6fd8;
  transform: translateY(-1px);
}

.clear-btn {
  background: #f8f9fa;
  color: #666;
  border: 1px solid #dee2e6;
}

.clear-btn:hover {
  background: #e9ecef;
  color: #333;
}

/* New item highlight effect */
.new-item {
  animation: newItemHighlight 2s ease-out;
}

@keyframes newItemHighlight {
  0% {
    transform: scale(1.02);
    box-shadow: 0 0 20px rgba(102, 126, 234, 0.3);
  }
  50% {
    transform: scale(1.01);
    box-shadow: 0 0 15px rgba(102, 126, 234, 0.2);
  }
  100% {
    transform: scale(1);
    box-shadow: none;
  }
}

/* Responsive Design */
@media (max-width: 992px) {
  .avatar-medium {
    width: 50%;
  }
}

@media (max-width: 768px) {
  .new-layout-container {
    padding: 10px;
  }

  .centered-avatar-section {
    margin-bottom: 20px;
  }

  .avatar-medium {
    width: 60%;
  }

  .control-btn {
    width: 44px;
    height: 44px;
    font-size: 16px;
  }

  .floating-video-controls {
    bottom: 16px;
    gap: 10px;
  }

  .no-results-actions {
    flex-direction: column;
    align-items: center;
  }

  .retry-btn,
  .clear-btn {
    width: 200px;
    justify-content: center;
  }

  .start-chat-card {
    padding: 30px 20px;
  }

  .start-chat-btn {
    padding: 14px 24px;
    font-size: 15px;
  }
}

@media (max-width: 576px) {
  .avatar-medium {
    width: 100%;
  }

  .products-grid {
    grid-template-columns: 1fr;
  }

  .no-results-title {
    font-size: 20px;
  }

  .no-results-message {
    font-size: 14px;
  }

  .start-chat-container {
    min-height: 50vh;
  }

  .start-chat-card {
    padding: 20px;
  }

  .start-chat-icon {
    font-size: 48px;
  }

  .start-chat-title {
    font-size: 20px;
  }

  .start-chat-description {
    font-size: 14px;
  }
}

/* GPU Hardware Acceleration for Smooth 60fps Micro-Animations */
.control-btn,
.floating-video-controls,
.avatar-video,
.start-chat-btn,
.new-item {
  will-change: transform;
  transform: translateZ(0);
  backface-visibility: hidden;
}
</style>
